import type {
  CellType,
  CellTypeConfidence,
  CellTypeReport,
  CellTypeSignal,
  CellTypesConfig,
} from "./types";
import {
  detectFromFormat,
  detectFromTextValue,
  detectFromValueRange,
  detectFromValueType,
} from "./direct";
import { inferFromFormula } from "./inference";
import { addressFromRC } from "../rules/audit/addressing";
import type { SheetModel, WorkbookModel } from "../model";
import { Origin, ValueKind } from "../model";

const DEFAULT_WEIGHTS = { high: 3, medium: 2, low: 1 } as const;
const DEFAULT_DIRECT_RULES = {
  valueType: true,
  numberFormat: true,
  textPattern: true,
  valueRange: true,
} as const;

/**
 * Pure (no Excel.run) cell-type analysis over a slice of the workbook
 * model. Used by:
 *   - the standalone scanner (workbook-wide, full inference)
 *   - the cell-type-conflicts audit rule (per-sheet, partial inference)
 *
 * Three passes:
 *   1. Per-cell direct signals (value-type / format / text pattern /
 *      value range) + queue usage-inference signals from formulas.
 *   2. Merge inferred signals into the per-cell reports. A signal
 *      targeting a cell outside the scanned set is dropped.
 *   3. Consensus / conflict / interesting flag per report.
 *
 * Value reconstruction for the report's `value` field is mildly lossy
 * on booleans and errors (the original true/false / specific "#…!"
 * literal isn't tracked separately by the substrate) — but signal
 * generation doesn't depend on those, so the cell-type verdict is
 * unchanged.
 *
 * `sheets` controls scope: pass `[sheet]` for the rule, or
 * `Array.from(model.sheets.values())` for the standalone scanner.
 * inferFromFormula's usage signals key on `Sheet!A1`, so signals
 * targeting sheets outside `sheets` get dropped in pass 2.
 */
export function analyzeCellTypes(
  sheets: SheetModel[],
  model: WorkbookModel,
  config?: CellTypesConfig
): CellTypeReport[] {
  const weights = config?.weights ?? DEFAULT_WEIGHTS;
  const directRules = config?.directRules ?? DEFAULT_DIRECT_RULES;
  const catalog = config?.catalog && config.catalog.size > 0 ? config.catalog : undefined;

  const reports = new Map<string, CellTypeReport>();
  const usageSignals = new Map<string, CellTypeSignal[]>();

  const formulaA1Strings = model.catalogs.formulaA1Strings;
  const numFmtStrings = model.catalogs.numFmtStrings;
  const textValueStrings = model.catalogs.textValueStrings;

  // ---- Pass 1: per-cell direct signals + queue usage inferences ----
  for (const sheet of sheets) {
    const { rows, cols, startRow, startCol } = sheet.bounds;
    const cellOrigin = sheet.cellOrigin;
    const cellValueKind = sheet.cellValueKind;
    const numericGrid = sheet.numericGrid;
    const numFmtIds = sheet.numFmtIds;
    const textValueIds = sheet.textValueIds;
    const formulaA1Ids = sheet.formulaA1Ids;

    for (let r = 0; r < rows; r++) {
      const rowBase = r * cols;
      for (let c = 0; c < cols; c++) {
        const idx = rowBase + c;
        const origin = cellOrigin[idx];
        if (origin === Origin.EMPTY) continue;

        const vk = cellValueKind[idx];
        const vt = mapValueKindToRangeValueType(vk);

        const addr = addressFromRC(startRow + r, startCol + c);
        const key = `${sheet.name}!${addr}`;

        const fmtId = numFmtIds[idx];
        const fmt = fmtId >= 0 ? numFmtStrings[fmtId] : undefined;

        // Reconstruct value per kind. Only NUMBER/DATE/TEXT need real
        // values for signal generation; BOOLEAN/ERROR get sentinels
        // for the report's `value` field but no signals key off them.
        let value: unknown;
        if (vk === ValueKind.NUMBER || vk === ValueKind.DATE) {
          value = numericGrid[idx];
        } else if (vk === ValueKind.TEXT) {
          const tid = textValueIds[idx];
          value = tid >= 0 ? textValueStrings[tid] : "";
        } else if (vk === ValueKind.BOOLEAN) {
          value = false;
        } else if (vk === ValueKind.ERROR) {
          value = "#REF!";
        } else {
          value = "";
        }

        const hasFormula = origin === Origin.FORMULA;
        const signals: CellTypeSignal[] = [];

        if (directRules.valueType) {
          const vtSig = detectFromValueType(vt);
          if (vtSig) signals.push(vtSig);
        }
        if (directRules.numberFormat) {
          signals.push(...detectFromFormat(fmt));
        }
        if (
          directRules.textPattern &&
          vk === ValueKind.TEXT &&
          typeof value === "string"
        ) {
          signals.push(...detectFromTextValue(value));
        }
        if (
          directRules.valueRange &&
          (vk === ValueKind.NUMBER || vk === ValueKind.DATE) &&
          typeof value === "number"
        ) {
          signals.push(...detectFromValueRange(value));
        }

        reports.set(key, {
          sheetName: sheet.name,
          address: addr,
          value,
          hasFormula,
          signals,
          consensus: null,
          conflict: false,
          interesting: false,
        });

        if (hasFormula) {
          const fId = formulaA1Ids[idx];
          if (fId >= 0) {
            inferFromFormula(formulaA1Strings[fId], sheet.name, usageSignals, catalog);
          }
        }
      }
    }
  }

  // ---- Pass 2: merge usage-inferred signals into reports ----
  for (const [key, sigs] of Array.from(usageSignals)) {
    const rep = reports.get(key);
    if (rep) rep.signals.push(...sigs);
    // Signal targeting a cell outside the scanned set is dropped.
  }

  // ---- Pass 3: consensus / conflict / interesting ----
  const out: CellTypeReport[] = [];
  for (const rep of Array.from(reports.values())) {
    if (rep.signals.length === 0) continue;
    const { consensus, conflict } = computeConsensus(rep.signals, weights);
    rep.consensus = consensus;
    rep.conflict = conflict;
    rep.interesting = computeInteresting(rep);
    out.push(rep);
  }

  out.sort((a, b) => a.sheetName.localeCompare(b.sheetName) || compareA1(a.address, b.address));
  return out;
}

function mapValueKindToRangeValueType(vk: number): Excel.RangeValueType {
  switch (vk) {
    case ValueKind.NUMBER:
    case ValueKind.DATE:
      return Excel.RangeValueType.double;
    case ValueKind.TEXT:
      return Excel.RangeValueType.string;
    case ValueKind.BOOLEAN:
      return Excel.RangeValueType.boolean;
    case ValueKind.ERROR:
      return Excel.RangeValueType.error;
    default:
      return Excel.RangeValueType.empty;
  }
}

function computeConsensus(
  signals: CellTypeSignal[],
  weights: Record<CellTypeConfidence, number>
): {
  consensus: CellType | null;
  conflict: boolean;
} {
  // Weighted vote per type
  const byType = new Map<CellType, number>();
  for (const s of signals) {
    byType.set(s.type, (byType.get(s.type) ?? 0) + weights[s.confidence]);
  }

  let bestType: CellType | null = null;
  let bestScore = 0;
  for (const [type, score] of byType) {
    if (score > bestScore) {
      bestScore = score;
      bestType = type;
    }
  }

  // Conflict: signals from two or more *families* at non-trivial confidence.
  // Low-confidence signals (loose text patterns, plausible date serials)
  // don't trigger conflicts on their own — they need corroboration.
  const families = new Set<string>();
  for (const s of signals) {
    if (s.confidence === "low") continue;
    families.add(family(s.type));
  }
  families.delete("empty");
  families.delete("error");
  families.delete("ignore");
  const conflict = families.size > 1;

  return { consensus: bestType, conflict };
}

function computeInteresting(rep: CellTypeReport): boolean {
  // Anything with a formula or a conflict is interesting.
  if (rep.hasFormula) return true;
  if (rep.conflict) return true;
  // Any signal beyond a bare valueType makes the cell interesting (format,
  // text pattern, value-range plausibility, or any usage inference).
  for (const s of rep.signals) {
    if (!s.source.startsWith("valueType:")) return true;
  }
  // Only valueType signals remain. Non-numeric value types (text, boolean,
  // error) ARE interesting — only plain numeric cells are filtered out.
  for (const s of rep.signals) {
    if (s.source !== "valueType:numeric") return true;
  }
  return false;
}

function family(t: CellType): string {
  switch (t) {
    case "date":
    case "datetime":
    case "time":
    case "number":
    case "integer":
    case "percent":
    case "currency":
      return "numeric";
    case "text":
      return "text";
    case "boolean":
      return "boolean";
    case "error":
      return "error";
    case "empty":
      return "empty";
    case "unknown":
      return "ignore";
  }
}

function compareA1(a: string, b: string): number {
  const pa = parseA1(a);
  const pb = parseA1(b);
  return pa.row - pb.row || pa.col - pb.col;
}

function parseA1(addr: string): { row: number; col: number } {
  const m = /^([A-Z]+)(\d+)$/.exec(addr);
  if (!m) return { row: 0, col: 0 };
  let col = 0;
  for (const ch of m[1]) col = col * 26 + (ch.charCodeAt(0) - 64);
  return { row: parseInt(m[2], 10), col };
}
